import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useForm } from "react-hook-form";
import { transformWorkHistoryToFormData } from "../utils/workHistoryTransform";
import { transformToRailsParams } from "../utils/railsParamsTransform";
import {
  getDefaultFormValues,
  getInitialMasterData,
} from "../config/defaultValues";
import {
  fetchMasterData,
  fetchFrameworkData,
  fetchBusinessSystemProductLanguageData,
  fetchCloudServiceProductData,
  fetchServerEnvironmentData,
  fetchMiddlewareProductData,
} from "../utils/apiClient";

const SkillSheetMainStepperContext = createContext();

/**
 * SkillSheetMainStepperのコンテキストフック
 * @returns {Object} コンテキストオブジェクト
 */
export const useSkillSheetMainStepper = () => {
  const context = useContext(SkillSheetMainStepperContext);
  if (!context) {
    throw new Error(
      "useSkillSheetMainStepper must be used within a SkillSheetMainStepperProvider"
    );
  }
  return context;
};

/**
 * SkillSheetMainStepperのプロバイダーコンポーネント
 * @param {Object} props - プロパティ
 * @param {React.ReactNode} props.children - 子コンポーネント
 * @param {string} props.csrfToken - CSRFトークン
 * @param {string} props.formAction - フォームアクション
 * @param {Object|null} props.workHistory - 既存の作業履歴（編集モード時）
 */
export const SkillSheetMainStepperProvider = ({
  children,
  csrfToken,
  formAction,
  workHistory = null,
}) => {
  const isEditMode = !!workHistory;

  // react-hook-formを使用してフォーム状態を管理
  const methods = useForm({
    mode: "onChange",
    defaultValues: getDefaultFormValues(),
  });

  // 状態管理
  const [masterData, setMasterData] = useState(getInitialMasterData());
  const [stepValidators, setStepValidators] = useState({});

  // 編集モードの場合、workHistoryからフォームデータを初期化
  useEffect(() => {
    if (isEditMode && workHistory) {
      const initialFormData = transformWorkHistoryToFormData(workHistory);

      // 参画中の場合、終了日を空文字（UI上で空表示）
      if (workHistory.participation_flag) {
        initialFormData.period_at = "";
      }
      methods.reset(initialFormData);
    }
  }, [workHistory, methods, isEditMode]);

  // ステップバリデーション関数
  const validateStep = useCallback((stepIndex, validatorFunction) => {
    setStepValidators((prev) => ({
      ...prev,
      [stepIndex]: validatorFunction,
    }));
  }, []);

  const runStepValidation = useCallback(
    (stepIndex) => {
      const validator = stepValidators[stepIndex];
      if (validator && typeof validator === "function") {
        return validator();
      }
      return true;
    },
    [stepValidators]
  );

  // マスタデータ取得関数をコンテキストにマップ
  const contextFetchMasterData = useCallback(async (dataType) => {
    const data = await fetchMasterData(dataType);
    setMasterData((prev) => ({
      ...prev,
      [dataType]: data,
    }));
  }, []);

  // フォーム送信処理
  const submitForm = useCallback(async () => {
    const formData = methods.getValues();
    const railsParams = transformToRailsParams(formData, workHistory);

    try {
      const form = createFormElement(
        railsParams,
        csrfToken,
        formAction,
        isEditMode
      );
      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      console.error("Form submission error:", error);
    }
  }, [csrfToken, formAction, methods, workHistory, isEditMode]);

  // コンテキスト値の構築
  const contextValue = {
    ...methods,
    masterData,
    fetchMasterData: contextFetchMasterData,
    fetchFrameworkData,
    fetchBusinessSystemProductLanguageData,
    fetchCloudServiceProductData,
    fetchServerEnvironmentData,
    fetchMiddlewareProductData,
    submitForm,
    validateStep,
    runStepValidation,
    isEditMode,
    workHistory,
  };

  return (
    <SkillSheetMainStepperContext.Provider value={contextValue}>
      {children}
    </SkillSheetMainStepperContext.Provider>
  );
};

/**
 * フォーム要素を作成する
 * @param {Object} railsParams - Railsパラメータ
 * @param {string} csrfToken - CSRFトークン
 * @param {string} formAction - フォームアクション
 * @param {boolean} isEditMode - 編集モードかどうか
 * @returns {HTMLFormElement} 作成されたフォーム要素
 */
const createFormElement = (railsParams, csrfToken, formAction, isEditMode) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = formAction;

  // 編集モードの場合はPATCHメソッドを追加
  if (isEditMode) {
    const methodInput = document.createElement("input");
    methodInput.type = "hidden";
    methodInput.name = "_method";
    methodInput.value = "patch";
    form.appendChild(methodInput);
  }

  // CSRF トークン
  const csrfInput = document.createElement("input");
  csrfInput.type = "hidden";
  csrfInput.name = "authenticity_token";
  csrfInput.value = csrfToken;
  form.appendChild(csrfInput);

  // user_idをフォームに含める
  const urlParams = new URLSearchParams(window.location.search);
  const userId = urlParams.get("user_id");
  if (userId) {
    const userIdInput = document.createElement("input");
    userIdInput.type = "hidden";
    userIdInput.name = "user_id";
    userIdInput.value = userId;
    form.appendChild(userIdInput);
  }

  // パラメータを再帰的に追加
  addInputsRecursively(form, railsParams);

  return form;
};

/**
 * オブジェクトを再帰的にフォーム入力として追加する
 * @param {HTMLFormElement} form - フォーム要素
 * @param {Object} obj - 追加するオブジェクト
 * @param {string} prefix - プレフィックス
 */
const addInputsRecursively = (form, obj, prefix = "") => {
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    const name = prefix ? `${prefix}[${key}]` : key;

    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      addInputsRecursively(form, value, name);
    } else {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value || "";
      form.appendChild(input);
    }
  });
};
